import React from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  InfoOutlined,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import getInitials from "../../../utils/getInitailasIMGUtils";
import { useNavigate } from "react-router-dom";
import TimeStamptoExp from "../../../utils/timestampConverter";
import {
  Z_TABLE_STICKY_COL_BODY,
  Z_TABLE_STICKY_COL_HEAD,
  Z_TABLE_STICKY_HEAD_CORNER,
  Z_TABLE_STICKY_HEAD_ROW,
} from "../../../constants/zIndex";
import SortingComponent from "../../../components/ShortingFN/SortingComponent";
import { MoreVert } from "@mui/icons-material";
import {
  isMfBuyTx,
  isMfSellTx,
  mfTxFullLabel,
} from "../../../utils/mf/mfTransactionTypes";
import { formatBuySellTxnTypeLabel } from "../../../utils/transactions/txnTypeDisplayLabel";
import {
  ALL_TXN_BENCHMARK_COLUMN_KEYS,
  resolveTxnBenchmarkGainValue,
  resolveTxnBenchmarkPercentValue,
} from "../../../utils/reports/transactionBenchmarkColumns";
import {
  formatTxnMetadataCurrency,
  formatTxnMetadataText,
  formatTxnMetadataUnits,
  isTxnMetadataColumn,
  TXN_COST_COLUMN_KEY_SET,
  TXN_METADATA_CURRENCY_KEYS,
  TXN_METADATA_SHOW_ZERO_CURRENCY_KEYS,
  TXN_METADATA_UNITS_KEYS,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  formatReportTypeColumnLabel,
  getReportHoldingTypeColor,
  isReportRealizedGainRow,
  isReportUnrealizedGainRow,
  resolveTxnIsSellForPnl,
} from "../../../utils/reports/reportDisplayLabels";
import {
  REPORT_COLORS,
  REPORT_TABLE_ELEMENT_SX,
  REPORT_TABLE_HEAD_ROW_SX,
  REPORT_TABLE_HEAD_SX,
  REPORT_TABLE_ROW_SX,
} from "./mfReportsTheme";
import MfReportCardList from "./MfReportCardList";
import { PROFILE_FONT } from "../../../constants/layout";
import { parseReturnMetricNumber, shouldDisplayReturnMetricAsDash } from "../../../utils/returnMetrics";
import { renderAmountCell } from "../../../components/Reusablecomponent/amountCellRender";

const REPORT_HEADER_TEXT = "#494646";
const REPORT_UNITS_NAV_COLOR = "#494646";
const REPORT_STICKY_FIRST_COL_WIDTH_PX = 200;
const REPORT_STICKY_SECOND_COL_WIDTH_PX = 140;

const parseColumnWidthPx = (
  value: string | number | undefined,
  fallback: number,
): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = parseInt(value.replace(/px$/i, "").trim(), 10);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
};

const hasDualStickyReportColumns = (
  headData: Array<{ key: string; value: string; locked?: boolean }>,
) =>
  Boolean(
    headData.length > 1 &&
      (headData[0] as { locked?: boolean })?.locked &&
      (headData[1] as { locked?: boolean })?.locked,
  );

const getReportStickyColumnSx = (
  headData: Array<{ key: string; value: string; locked?: boolean; minWidth?: string | number; stickyLeft?: string | number }>,
  idx: number,
  backgroundColor: string,
  zIndex: number,
  options?: { freezeHeaderRow?: boolean; isHeaderCell?: boolean },
) => {
  const { freezeHeaderRow = false, isHeaderCell = false } = options ?? {};
  const dualSticky = hasDualStickyReportColumns(headData);
  const col = headData[idx] as {
    minWidth?: string | number;
    stickyLeft?: string | number;
    key?: string;
  };
  const freezeTopSx =
    freezeHeaderRow && isHeaderCell
      ? {
          top: 0,
        }
      : {};
  /** Opaque bottom edge — soft box-shadow leaves a white hairline when rows scroll beneath. */
  const freezeBottomEdgeSx =
    freezeHeaderRow && isHeaderCell
      ? { borderBottom: `2px solid ${REPORT_COLORS.tableHeader}` }
      : {};

  if (idx === 0) {
    const widthPx = dualSticky
      ? parseColumnWidthPx(col?.minWidth, REPORT_STICKY_FIRST_COL_WIDTH_PX)
      : parseColumnWidthPx(
          col?.minWidth,
          col?.key === "schemeName" ||
            col?.key === "holdingName" ||
            col?.key === "name" ||
            col?.key === "insurerName" ||
            col?.key === "primaryCategory" ||
            col?.key === "promoter" ||
            col?.key === "transactiondate" ||
            col?.key === "transactionDate"
            ? REPORT_STICKY_FIRST_COL_WIDTH_PX
            : 120,
        );
    const cornerZ =
      freezeHeaderRow && isHeaderCell ? Z_TABLE_STICKY_HEAD_CORNER : zIndex;
    return {
      position: "sticky",
      left: 0,
      zIndex: cornerZ,
      // Solid opaque paint so scrolled cells never show through.
      backgroundColor: `${backgroundColor} !important`,
      backgroundImage: "none",
      backgroundClip: "padding-box",
      minWidth: `${widthPx}px`,
      maxWidth: `${widthPx}px`,
      width: `${widthPx}px`,
      boxShadow: "2px 0 4px -3px rgba(0, 0, 0, 0.12)",
      ...freezeTopSx,
      ...freezeBottomEdgeSx,
    };
  }

  if (idx === 1 && dualSticky) {
    const col0Width = parseColumnWidthPx(
      (headData[0] as { minWidth?: string | number })?.minWidth,
      REPORT_STICKY_FIRST_COL_WIDTH_PX,
    );
    const widthPx = parseColumnWidthPx(
      col?.minWidth,
      REPORT_STICKY_SECOND_COL_WIDTH_PX,
    );
    const leftPx = parseColumnWidthPx(col?.stickyLeft, col0Width);
    const cornerZ =
      freezeHeaderRow && isHeaderCell ? Z_TABLE_STICKY_HEAD_CORNER : zIndex;
    return {
      position: "sticky",
      left: `${leftPx}px`,
      zIndex: cornerZ,
      backgroundColor: `${backgroundColor} !important`,
      backgroundImage: "none",
      backgroundClip: "padding-box",
      minWidth: `${widthPx}px`,
      maxWidth: `${widthPx}px`,
      width: `${widthPx}px`,
      boxShadow: "2px 0 4px -3px rgba(0, 0, 0, 0.12)",
      ...freezeTopSx,
      ...freezeBottomEdgeSx,
    };
  }

  if (freezeHeaderRow && isHeaderCell) {
    return {
      position: "sticky" as const,
      top: 0,
      zIndex: Z_TABLE_STICKY_HEAD_ROW,
      backgroundColor,
      ...freezeBottomEdgeSx,
    };
  }

  return {};
};

const getPnlColor = (value: number) =>
  value >= 0 ? REPORT_COLORS.profit : REPORT_COLORS.loss;

const pnlTextStyle = (value: number): React.CSSProperties => ({
  color: getPnlColor(value),
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
});

const pnlSignPrefix = (value: number) =>
  value > 0 ? "+" : value < 0 ? "-" : "";

const renderPnlAmount = (value: number) => (
  <span style={pnlTextStyle(value)}>
    {pnlSignPrefix(value)}₹
    {Math.abs(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </span>
);

const renderPnlPercent = (value: number) => (
  <span style={pnlTextStyle(value)}>
    {pnlSignPrefix(value)}
    {Math.abs(value).toFixed(2)}%
  </span>
);

const renderCombinedPnlCell = (
  data: Record<string, unknown>,
  key: "unrealisedPNLCombined" | "realisedPNLCombined",
) => {
  const isUnrealised = key === "unrealisedPNLCombined";
  const amountRaw = isUnrealised
    ? data?.unrealisedPNL ?? data?.unrealisedPnL ?? data?.unrealizedPNL
    : data?.realisedPnl ??
      data?.realisedPnL ??
      data?.realisedPNL ??
      data?.realizedPNL;
  const percentRaw = isUnrealised
    ? data?.unrealisedPNLPercentage ?? data?.unrealisedPnLPercentage
    : data?.realisedPNLPercentage ?? data?.realisedPnLPercentage;

  const hasAmount =
    amountRaw != null && amountRaw !== "" && amountRaw !== "-" && Number.isFinite(Number(amountRaw));
  const hasPercent =
    percentRaw != null &&
    percentRaw !== "" &&
    percentRaw !== "-" &&
    Number.isFinite(Number(percentRaw));

  if (!hasAmount && !hasPercent) {
    const raw = data?.[key];
    if (raw == null || raw === "" || raw === "-") return "-";
    if (typeof raw === "string" && /NaN/i.test(raw)) return "-";
    return String(raw);
  }

  const amount = hasAmount ? Number(amountRaw) : 0;
  const percent = hasPercent ? Number(percentRaw) : 0;
  if (Object.is(amount, 0) && Object.is(percent, 0)) return "-";

  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {hasAmount ? renderPnlAmount(amount) : "-"}
      <span style={{ color: REPORT_COLORS.mutedText }}> / </span>
      {hasPercent ? renderPnlPercent(percent) : "-"}
    </span>
  );
};

const unitsNavStyle: React.CSSProperties = {
  color: REPORT_UNITS_NAV_COLOR,
  fontWeight: 400,
  fontVariantNumeric: "tabular-nums",
};

type ReportTableColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: string | number;
  align?: string;
  stickyLeft?: string | number;
};

interface TableComponentProps {
  headData: ReportTableColumn[];
  subcategory?: any;
  bodyData: Array<any>;
  unSortdata?: any;
  findHighestInterestRates?: (schemes: any) => number;
  navigateDetails?: (id: string) => void;
  handleMenuClick?: (
    event: React.MouseEvent<HTMLElement>,
    rate1: any,
    rate2: any,
  ) => void;
  modalMenuClick?: any;
  segment?: string;
  reportType?: string;
  initials?: boolean;
}

const getMfTxnTypeRaw = (data: Record<string, any>): string => {
  const raw = String(
    data?.transactionType ??
      data?.transType ??
      data?.txnType ??
      data?.transactiontype ??
      "",
  ).trim();
  if (raw) return raw;
  const category = String(data?.category ?? "").trim();
  if (category.toLowerCase().includes("sip")) return "SIP";
  return "";
};

const isSellOrRedemptionTxn = (data: Record<string, any>): boolean =>
  isMfSellTx(getMfTxnTypeRaw(data)) ||
  (() => {
    const txnType = getMfTxnTypeRaw(data).toLowerCase();
    if (!txnType) return false;
    return (
      txnType.includes("sell") ||
      txnType.includes("redemption") ||
      txnType.includes("redeem")
    );
  })();

const isMfInflowTxnType = (data: Record<string, any>): boolean => {
  const raw = getMfTxnTypeRaw(data);
  const lower = raw.toLowerCase();
  if (!lower) return false;
  if (lower.includes("sip")) return true;
  return (
    isMfBuyTx(raw) ||
    lower.includes("purchase") ||
    lower.includes("buy") ||
    lower.includes("lumpsum")
  );
};

const isMfOutflowTxnType = (data: Record<string, any>): boolean => {
  const raw = getMfTxnTypeRaw(data);
  const lower = raw.toLowerCase();
  return (
    isMfSellTx(raw) ||
    lower.includes("redemption") ||
    lower.includes("sell") ||
    lower.includes("redeem") ||
    lower.includes("swp")
  );
};

const getMfTxnTypeDisplayLabel = (data: Record<string, any>): string => {
  const forced =
    (data as any)?.displayTransactionType ?? (data as any)?.displayTransType;
  if (forced && String(forced).trim()) {
    return formatBuySellTxnTypeLabel(String(forced).trim());
  }
  const raw = getMfTxnTypeRaw(data);
  if (!raw) return "-";
  const labeled = mfTxFullLabel(raw);
  const base = labeled !== "-" ? labeled : raw;
  return formatBuySellTxnTypeLabel(base);
};

const MF_POINT_TO_POINT_REPORT = "Point to Point Return";

const isMfTransactionReport = (reportType?: string) =>
  reportType === "Transaction report";

const isMfPointToPointReport = (reportType?: string, segment?: string) =>
  reportType === MF_POINT_TO_POINT_REPORT &&
  (segment === "Mutual Funds" || segment === "SIF");

const isMfTxnStyleReport = (reportType?: string, segment?: string) =>
  isMfTransactionReport(reportType) ||
  isMfPointToPointReport(reportType, segment);

const isStockP2pReport = (reportType?: string, segment?: string) =>
  reportType === MF_POINT_TO_POINT_REPORT && segment === "STOCK";

const isAnyP2pReport = (reportType?: string, segment?: string) =>
  isMfPointToPointReport(reportType, segment) ||
  isStockP2pReport(reportType, segment);

const isTxnBenchmarkColumn = (key: string) =>
  ALL_TXN_BENCHMARK_COLUMN_KEYS.includes(key);

const TXN_BENCHMARK_CELL_SX = {
  whiteSpace: "nowrap",
  fontSize: "12px",
  lineHeight: 1.3,
  paddingLeft: "8px",
  paddingRight: "8px",
  fontVariantNumeric: "tabular-nums",
} as const;

const MF_TXN_REPORT_WRAP_KEYS = new Set([
  "transactionType",
  "transType",
  "txnType",
  "category",
  "folioNo",
]);

const MF_TXN_TYPE_COLUMN_KEYS = new Set([
  "transactionType",
  "transType",
  "txnType",
]);

const MF_TXN_TYPE_CELL_STYLE: React.CSSProperties = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  lineHeight: 1.35,
  display: "block",
  maxWidth: "100%",
  overflow: "visible",
};

const REPORT_SCHEME_NAME_CELL_STYLE: React.CSSProperties = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  lineHeight: 1.35,
  display: "block",
  maxWidth: "100%",
  overflow: "visible",
};

const getMfTxnReportColumnMinWidth = (
  key: string,
  col?: { minWidth?: string | number },
  reportType?: string,
): string | undefined => {
  if (col?.minWidth != null && col.minWidth !== "") {
    return typeof col.minWidth === "number" ? `${col.minWidth}px` : String(col.minWidth);
  }
  const isP2p = reportType === MF_POINT_TO_POINT_REPORT;
  switch (key) {
    case "transactiondate":
    case "transactionDate":
      return isP2p ? "88px" : "112px";
    case "schemeName":
      return isP2p ? "168px" : "220px";
    case "symbol":
      return isP2p ? "112px" : undefined;
    case "exchange":
      return isP2p ? "88px" : undefined;
    case "category":
      return "96px";
    case "transactionType":
    case "transType":
    case "txnType":
      return isP2p ? "96px" : "120px";
    case "type":
      return isP2p ? "132px" : undefined;
    case "folioNo":
      return isP2p ? "128px" : "96px";
    case "units":
      return isP2p ? "112px" : "100px";
    case "amount":
    case "netPurchase":
    case "investedAmount":
    case "currentAmount":
      return isP2p ? "152px" : "128px";
    case "avgPrice":
    case "averageNav":
      return isP2p ? "112px" : "96px";
    case "currentNAV":
    case "purchaseNAV":
      return isP2p ? "128px" : "112px";
    case "unrealisedPNL":
    case "realisedPnl":
      return isP2p ? "132px" : undefined;
    case "unrealisedPNLPercentage":
    case "realisedPNLPercentage":
      return isP2p ? "120px" : undefined;
    case "unrealisedPNLCombined":
    case "realisedPNLCombined":
      return isP2p ? "168px" : undefined;
    case "days":
      return isP2p ? "88px" : "88px";
    case "gainLoss":
      return "100px";
    case "gainLossPercentage":
      return "72px";
    case "nifty50GainLoss":
    case "bseSensexGainLoss":
      return isP2p ? "96px" : "80px";
    case "nifty50GainLossPercentage":
    case "nifty500GainLossPercentage":
    case "niftyBankGainLossPercentage":
      return isP2p ? "84px" : "72px";
    case "bseSensexGainLossPercentage":
      return isP2p ? "84px" : "76px";
    case "niftyMidCap100GainLossPercentage":
      return isP2p ? "96px" : "88px";
    case "niftyNext50GainLossPercentage":
      return isP2p ? "90px" : "84px";
    case "memberName":
    case "investorDisplay":
      return isP2p ? "120px" : "120px";
    default:
      return undefined;
  }
};

const getMfTxnReportCellWidthSx = (
  key: string,
  col?: { minWidth?: string | number },
  reportType?: string,
  segment?: string,
): Record<string, string> | undefined => {
  const width = getMfTxnReportColumnMinWidth(key, col, reportType);
  if (!width) return undefined;
  if (isTxnBenchmarkColumn(key) || isAnyP2pReport(reportType, segment)) {
    return { minWidth: width };
  }
  if (isMfPointToPointReport(reportType, segment) && key === "folioNo") {
    return { minWidth: width, width: "max-content" };
  }
  return { minWidth: width, maxWidth: width, width };
};

const REPORT_EXPOSURE_COLUMN_KEYS = new Set(["percentageShare", "exposurePct"]);

const isPmsBreakdownReport = (
  segment?: string,
  reportType?: string,
): boolean =>
  segment === "PMS" &&
  (reportType === "Sector report" || reportType === "Cap report");

const isConsolidatedReport = (reportType?: string): boolean =>
  reportType === "Consolidated Report";

const getSectorReportCellSx = (
  isSectorReport: boolean,
  key: string,
  isPmsBreakdown?: boolean,
): Record<string, string> | undefined => {
  if (!isSectorReport && !isPmsBreakdown) return undefined;
  if (REPORT_EXPOSURE_COLUMN_KEYS.has(key)) {
    return {
      whiteSpace: "nowrap",
      wordBreak: "normal",
      overflowWrap: "normal",
      minWidth: isPmsBreakdown ? "160px" : "88px",
      textAlign: isPmsBreakdown ? "left" : "right",
    };
  }
  if (key === "amount") {
    return { whiteSpace: "nowrap" };
  }
  if (isPmsBreakdown && key === "isin") {
    return { whiteSpace: "nowrap", textAlign: "center" };
  }
  return undefined;
};

const getSectorReportColumnWidth = (
  isSectorReport: boolean,
  headData: Array<{ key: string }>,
  key: string,
  segment?: string,
  reportType?: string,
): string | undefined => {
  const isPmsBreakdown = isPmsBreakdownReport(segment, reportType);
  if (isPmsBreakdown) {
    const isCap = reportType === "Cap report";
    const widths: Record<string, string> = isCap
      ? {
          schemeName: "12%",
          marketCap: "12%",
          isin: "14%",
          stockName: "18%",
          exposurePct: "22%",
          memberName: "12%",
        }
      : {
          schemeName: "12%",
          isin: "14%",
          stockName: "20%",
          sector: "18%",
          percentageShare: "22%",
          memberName: "14%",
        };
    return widths[key];
  }

  if (!isSectorReport) return undefined;
  if (headData.some((c) => TXN_COST_COLUMN_KEY_SET.has(c.key))) {
    return undefined;
  }
  const hasPct = headData.some((c) => c.key === "percentageShare");
  const hasMember = headData.some((c) => c.key === "memberName");
  if (hasPct && hasMember) {
    if (key === "sector") return "34%";
    if (key === "amount") return "30%";
    if (key === "percentageShare") return "14%";
    if (key === "memberName") return "22%";
    return undefined;
  }
  if (hasPct) {
    if (key === "sector") return "38%";
    if (key === "amount") return "38%";
    if (key === "percentageShare") return "24%";
    return undefined;
  }
  if (key === "sector") return "40%";
  if (key === "amount") return "35%";
  if (key === "memberName") return "25%";
  return undefined;
};

/** MF P2P column spacing — keep nowrap on dense labels without squeezing padding. */
const getMfP2pColumnSpacingSx = (
  isMfP2pTable: boolean,
  key: string,
): Record<string, string | number> | undefined => {
  if (!isMfP2pTable) return undefined;
  if (key === "folioNo" || key === "type") {
    return {
      whiteSpace: "nowrap",
      wordBreak: "normal",
      overflowWrap: "normal",
      overflow: "visible",
    };
  }
  return undefined;
};

const shouldHideCurrentValueForTxn = (
  key: string,
  data: any,
  reportType?: string,
): boolean => {
  // MF/SIF "Transaction Report" shows API currentValue for buys and sells.
  if (isMfTransactionReport(reportType)) return false;
  return (
    (key === "currentValue" ||
      key === "currentAmount" ||
      key === "currentVal") &&
    isSellOrRedemptionTxn(data)
  );
};

const parseReportTableInrAmount = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "" || raw === "-") {
    return null;
  }
  const n =
    typeof raw === "number"
      ? raw
      : Number(String(raw).replace(/[₹,\s]/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

/** Match holdings: full ₹ amount below 1 Cr; ₹X Cr + info tooltip at/above 1 Cr. */
const formatReportTableInrCell = (raw: unknown) => {
  const amount = parseReportTableInrAmount(raw);
  if (amount === null) return "-";
  return renderAmountCell(amount, { align: "right" });
};

/** AIF / PMS consolidated report amount columns. */
const REPORT_CONSOLIDATED_INR_KEYS = new Set([
  "closingValue",
  "commitment",
  "unfundedCommitment",
  "amountCalled",
  "amountDistributed",
  "recallableDistributions",
  "balanceRecallableCapital",
  "costBasis",
  "costBasisAdjustement",
  "taxBasis",
  "investedCost",
  "valuation",
  "income",
  "expenses",
]);

const MainTableLayout: React.FC<TableComponentProps> = ({
  headData,
  subcategory,
  bodyData,
  unSortdata,
  modalMenuClick,
  initials,
  segment,
  reportType,
}) => {
  const isReportTable = Boolean(segment || reportType);
  const tableHeaderBg = isReportTable
    ? REPORT_COLORS.tableHeader
    : "#F9FAFB";
  const tableHeaderText = isReportTable
    ? REPORT_COLORS.tableHeaderText
    : REPORT_HEADER_TEXT;
  const tableHeaderTone = isReportTable ? "dark" : "light";
  const isSectorReport = reportType === "Sector report";
  const isPmsBreakdown = isPmsBreakdownReport(segment, reportType);
  const isConsolidated = isConsolidatedReport(reportType);
  const hasTxnCostColumns = headData.some((c) =>
    TXN_COST_COLUMN_KEY_SET.has(c.key),
  );
  const useBreakdownTableLayout =
    (isSectorReport || isPmsBreakdown) && !hasTxnCostColumns;
  const isMfTxnReport = isMfTxnStyleReport(reportType, segment);
  const isMfP2pTable = isMfPointToPointReport(reportType, segment);
  const isStockP2pTable = isStockP2pReport(reportType, segment);
  const isAnyP2pTable = isMfP2pTable || isStockP2pTable;
  const theme = useTheme();
  const isMobileReportTable = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileCardView = useMediaQuery(theme.breakpoints.down("md"));
  const useReportStickyColumns = isReportTable && !isMobileReportTable;
  const useReportFreezeHeader = isReportTable && !isMobileCardView;
  const [open, setOpen] = React.useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();

  const maxPmsExposurePct = React.useMemo(() => {
    if (!isPmsBreakdown || !Array.isArray(bodyData)) return 0;
    let max = 0;
    for (const row of bodyData) {
      const raw =
        row?.percentageShare ?? row?.exposurePct ?? row?.exposure;
      const num = Number(String(raw ?? "").replace(/%/g, "").trim());
      if (Number.isFinite(num) && num > max) max = num;
    }
    return max;
  }, [bodyData, isPmsBreakdown]);
  const handleToggle = (id: string) => {
    setOpen((prevOpen) => ({ ...prevOpen, [id]: !prevOpen[id] }));
  };

  const navigateDetails = (id: number) => {
    // // console.logn click', id);
    navigate(`/fd-details/${id}`);
  };

  const convertDaysToReadableFormat = (
    days: number,
    schemeName: string,
  ): string => {
    if (schemeName && schemeName?.includes("HDFC")) {
      if (days <= 90) {
        return `${days} day${days > 1 ? "s" : ""}`;
      }
    } else {
      return days <= 1 ? days + " month" : days + " months"; // Dynamic value for 12 months, approximated as 12 * 30 = 360 days
    }
    const today = new Date();
    let currentYear = today.getFullYear();
    let currentMonth = today.getMonth(); // Month is 0-based (0 = January, 11 = December)
    let currentDay = today.getDate();

    // Helper function to get the number of days in a given month and year
    const daysInMonth = (month: number, year: number): number => {
      return new Date(year, month + 1, 0).getDate();
    };

    // Determine the month length to use based on the current day
    let monthLength = daysInMonth(currentMonth, currentYear);
    if (currentDay > 20) {
      currentMonth += 1;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear += 1;
      }
      monthLength = daysInMonth(currentMonth, currentYear);
    }

    let totalMonths = 0;
    let remainingDays = days;
    let futureDate = new Date(today);

    // Calculate total months and remaining days
    while (remainingDays >= monthLength) {
      totalMonths += 1;
      remainingDays -= monthLength;
      currentMonth += 1;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear += 1;
      }
      monthLength = daysInMonth(currentMonth, currentYear);
    }

    // Calculate the exact future date
    futureDate.setMonth(today.getMonth() + totalMonths);
    futureDate.setDate(today.getDate() + remainingDays);

    // Format the readable string
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    let readableFormat = "";
    if (years > 0) readableFormat += `${years} yr${years > 1 ? "s" : ""} `;
    if (months > 0) readableFormat += `${months} mon `;
    if (remainingDays > 0)
      readableFormat += `${remainingDays} day${remainingDays > 1 ? "s" : ""} `;

    return readableFormat.trim();
  };

  const REPORT_INVESTED_AMOUNT_CELL_KEYS = new Set([
    "costValue",
    "investedAmount",
    "amount",
    "netPurchase",
    "purchaseValue",
    "purchaseAmount",
  ]);

  const formatCellData = (key: string, data: any) => {
    if (
      REPORT_INVESTED_AMOUNT_CELL_KEYS.has(key) &&
      isReportRealizedGainRow(data)
    ) {
      return "-";
    }
    if (
      (key === "currentAmount" ||
        key === "currentValue" ||
        key === "currentVal") &&
      isReportRealizedGainRow(data)
    ) {
      return "-";
    }
    const unrealisedPnlKeys = new Set([
      "unrealisedPNL",
      "unrealisedPnL",
      "unrealizedPNL",
      "unrealisedPNLPercentage",
      "unrealisedPnLPercentage",
      "unrealizedPNLPercentage",
    ]);
    if (unrealisedPnlKeys.has(key) && isReportRealizedGainRow(data)) {
      return "-";
    }
    const realisedPnlKeys = new Set([
      "realisedPnl",
      "realisedPnL",
      "realisedPNL",
      "realizedPnl",
      "realizedPnL",
      "realizedPNL",
      "realisedPNLPercentage",
      "realisedPnLPercentage",
      "realizedPNLPercentage",
    ]);
    if (realisedPnlKeys.has(key) && isReportUnrealizedGainRow(data)) {
      return "-";
    }
    const txnIsSell = resolveTxnIsSellForPnl(data);
    if (unrealisedPnlKeys.has(key) && txnIsSell === true) {
      return "-";
    }
    if (realisedPnlKeys.has(key) && txnIsSell === false) {
      return "-";
    }
    if (!data) return "-";
    if (shouldHideCurrentValueForTxn(key, data, reportType)) return "-";
    if (REPORT_CONSOLIDATED_INR_KEYS.has(key)) {
      return formatReportTableInrCell(data?.[key]);
    }
    if (isTxnMetadataColumn(key)) {
      if (key === "dpName") {
        return (
          <span className="text-gray-800" style={{ wordBreak: "break-word" }}>
            {formatTxnMetadataText(data?.[key])}
          </span>
        );
      }
      if (TXN_METADATA_CURRENCY_KEYS.has(key)) {
        return (
          <span
            style={{
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTxnMetadataCurrency(data?.[key], {
              showZero: TXN_METADATA_SHOW_ZERO_CURRENCY_KEYS.has(key),
            })}
          </span>
        );
      }
      if (TXN_METADATA_UNITS_KEYS.has(key)) {
        return (
          <span
            style={{
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTxnMetadataUnits(data?.[key])}
          </span>
        );
      }
    }
    switch (key) {
      case "grossInterest": // Key for 'Interest Earned'
        const interestEarned =
          (Number(data?.returnAmount) || 0) - (Number(data?.amount) || 0);
        return (
          <span className="font-medium text-green-600 whitespace-nowrap">
            ₹
            {interestEarned.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "amount":
        return formatReportTableInrCell(data?.amount);
      case "saleValue":
        return formatReportTableInrCell(
          data?.saleValue ?? data?.amount,
        );
      case "value":
        return (
          <span className="font-medium text-gray-900 whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
            ₹
            {Number(data?.value || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "monthlyReturn":
        const monthlyReturnValue = Number(data?.monthlyReturn || 0);
        return (
          <span
            className="whitespace-nowrap"
            style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}
          >
            ₹
            {monthlyReturnValue.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "investmentTenure":
        return `${convertDaysToReadableFormat(
          Number(data?.investmentTenure) || 0,
          data?.schemeName || "",
        )}`;
      case "returnAmount":
        return (
          <span className="font-medium text-gray-900 whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
            ₹
            {Number(data?.returnAmount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "transactionType":
      case "transType":
      case "txnType": {
        const rawLabel = getMfTxnTypeDisplayLabel(data);
        const displayLabel = isReportTable
          ? formatReportTypeColumnLabel(rawLabel, rawLabel)
          : rawLabel;
        const holdingTypeColor = isReportTable
          ? getReportHoldingTypeColor(displayLabel)
          : undefined;
        const inflow = !holdingTypeColor && isMfInflowTxnType(data);
        const outflow = !holdingTypeColor && isMfOutflowTxnType(data);
        return (
          <span
            style={{
              color:
                holdingTypeColor ??
                (inflow
                  ? REPORT_COLORS.profit
                  : outflow
                    ? REPORT_COLORS.realized
                    : isReportTable
                      ? REPORT_COLORS.primaryText
                      : "#111827"),
              fontWeight: 500,
              ...(isMfTxnReport ? MF_TXN_TYPE_CELL_STYLE : {
                whiteSpace: "nowrap",
                wordBreak: "normal",
                lineHeight: 1.35,
                display: "inline-block",
                maxWidth: "100%",
              }),
            }}
          >
            {displayLabel}
          </span>
        );
      }
      case "type": {
        const v = String(data?.type ?? "-").trim();
        const displayLabel = isReportTable
          ? formatReportTypeColumnLabel(v)
          : formatBuySellTxnTypeLabel(v);
        const color =
          (isReportTable && getReportHoldingTypeColor(displayLabel)) ||
          (isReportTable ? REPORT_COLORS.primaryText : "#111827");
        return (
          <span
            style={{
              color,
              fontWeight: 500,
              whiteSpace: isMfP2pTable ? "nowrap" : "normal",
              wordBreak: isMfP2pTable ? "normal" : "break-word",
              lineHeight: 1.35,
              display: "inline-block",
              maxWidth: "100%",
            }}
          >
            {displayLabel}
          </span>
        );
      }
      case "netPurchase":
        return (
          <span
            className="whitespace-nowrap"
            style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}
          >
            ₹
            {Number(data?.netPurchase || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "weekelyReturn":
      case "weeklyReturn": {
        const raw = data?.weeklyReturn ?? data?.weekelyReturn;
        if (raw === null || raw === undefined || raw === "") return "-";
        const num = Number(raw);
        if (!Number.isFinite(num)) return "-";
        return (
          <span
            className="whitespace-nowrap"
            style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}
          >
            {num.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        );
      }
      case "marketValue":
        return (
          <span className="font-medium text-gray-900 whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
            ₹
            {Number(data?.marketValue || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "absoluteReturn": {
        const absoluteReturnValue = Number(data?.absoluteReturn || 0);
        if (isReportTable) return renderPnlAmount(absoluteReturnValue);
        return (
          <span
            className="whitespace-nowrap"
            style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}
          >
            ₹
            {absoluteReturnValue.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
      case "benchmarkAR":
        return (
          <span className="font-medium text-gray-900 whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
            {Number(data?.benchmarkAR || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "tenure": {
        const raw = data?.tenure;

        if (!raw) return "-";
        if (typeof raw === "string" && isNaN(Number(raw))) return raw;

        const num = Number(raw);
        const label = num === 1 ? "Month" : "Months";

        return `${num} ${label}`;
      }
      case "gainLossPercentage": {
        const raw = data?.gainLossPercentage;

        if (raw === null || raw === undefined || raw === "") return "-";

        let num: number;
        if (typeof raw === "string" && raw.includes("%")) {
          num = parseFloat(raw.replace("%", ""));
        } else {
          num = Number(raw);
        }
        if (isNaN(num)) return "-";

        return renderPnlPercent(num);
      }
      case "roi": {
        const raw = data?.roi;

        if (raw === null || raw === undefined || raw === "") return "-";

        // If it's already a string containing %, return as-is
        if (typeof raw === "string" && raw.includes("%")) {
          return raw;
        }

        const num = Number(raw);

        return isNaN(num) ? "-" : `${num}%`;
      }
      case "gainLossPerc": {
        const raw = data?.gainLossPerc;

        if (raw === null || raw === undefined || raw === "") return "-";

        // Parse the value
        let num: number;
        if (typeof raw === "string" && raw.includes("%")) {
          num = parseFloat(raw.replace("%", ""));
        } else {
          num = Number(raw);
        }

        if (isNaN(num)) return "-";

        return renderPnlPercent(num);
      }
      case "weekelyReturnPercentage":
      case "weeklyReturnPercentage": {
        const raw =
          data?.weeklyReturnPercentage ?? data?.weekelyReturnPercentage;

        if (raw === null || raw === undefined || raw === "") return "-";
        if (typeof raw === "string" && raw.includes("%")) return raw;

        const num = Number(raw);
        if (!Number.isFinite(num)) return "-";
        return (
          <span
            className="whitespace-nowrap"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {num.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        );
      }

      case "cagr": {
        if (shouldDisplayReturnMetricAsDash(data?.cagr)) return "-";
        const num = parseReturnMetricNumber(data?.cagr);
        if (num === null) return "-";
        return renderPnlPercent(num);
      }

      case "xirr": {
        if (shouldDisplayReturnMetricAsDash(data?.xirr)) return "-";
        const num = parseReturnMetricNumber(data?.xirr);
        if (num === null) return "-";
        return renderPnlPercent(num);
      }

      case "absoluteReturnPerc": {
        const raw = data?.absoluteReturnPerc;

        if (raw === null || raw === undefined || raw === "") return "-";
        if (typeof raw === "string" && raw.includes("%")) return raw;

        const num = Number(raw);
        if (isReportTable) return isNaN(num) ? "-" : renderPnlPercent(num);
        return isNaN(num) ? "-" : `${num.toFixed(2)}%`;
      }
      case "monthlyReturnPercentage": {
        const raw = data?.monthlyReturnPercentage;

        if (raw === null || raw === undefined || raw === "") return "-";
        if (typeof raw === "string" && raw.includes("%")) return raw;

        const num = Number(raw);
        return isNaN(num) ? "-" : `${num.toFixed(2)}%`;
      }

      case "interesetRate":
        return (
          <span className="font-medium text-blue-600">
            {data?.interesetRate ? `${data.interesetRate}%` : "-"}
          </span>
        );
      case "initials":
        return getInitials((data?.bankName || data?.name || "") as string);
      case "createdAt":
        return data?.createdAt ?? "-";
      case "endDate":
        return data?.endDate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.endDate.toString()) // Check if the endDate is in DD/MM/YYYY format
            ? data.endDate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(Number(data.endDate)) // Otherwise, format it
          : "-";
      case "transactiondate":
        return data?.transactiondate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.transactiondate.toString()) // Check if the transactiondate is in DD/MM/YYYY format
            ? data.transactiondate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(
                Number(data.transactiondate),
              ) // Otherwise, format it
          : "-";
      case "transactionDate":
        return data?.transactionDate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.transactionDate.toString()) // Check if the transactionDate is in DD/MM/YYYY format
            ? data.transactionDate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(
                Number(data.transactionDate),
              ) // Otherwise, format it
          : "-";
      case "maturityDate":
        return data?.maturityDate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.maturityDate.toString()) // Check if the transactionDate is in DD/MM/YYYY format
            ? data.maturityDate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(Number(data.maturityDate)) // Otherwise, format it
          : "-";
      case "updatedDate":
      case "valuationDate": {
        const raw = data?.[key];
        if (
          raw == null ||
          raw === "" ||
          raw === "-" ||
          raw === 0 ||
          raw === "0"
        ) {
          return "-";
        }
        const asStr = String(raw).trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
        const ms = Number(raw);
        if (Number.isFinite(ms) && ms > 0) {
          const formatted = TimeStamptoExp.formatDateFromTimestamp(
            ms < 1e11 ? ms * 1000 : ms,
          );
          if (
            formatted &&
            !String(formatted).toLowerCase().includes("invalid")
          ) {
            return formatted;
          }
        }
        return asStr;
      }
      case "purchaseDate": {
        const raw = data?.purchaseDate;
        if (
          raw == null ||
          raw === "" ||
          raw === "-" ||
          raw === 0 ||
          raw === "0"
        ) {
          return "-";
        }
        const asStr = String(raw).trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
        const ms = Number(raw);
        if (!Number.isFinite(ms) || ms <= 0) return "-";
        const formatted = TimeStamptoExp.formatDateFromTimestamp(ms);
        if (
          !formatted ||
          String(formatted).toLowerCase().includes("invalid")
        ) {
          return "-";
        }
        return formatted;
      }
      case "currentDate": {
        const raw = data?.currentDate;
        if (
          raw == null ||
          raw === "" ||
          raw === "-" ||
          raw === 0 ||
          raw === "0"
        ) {
          return "-";
        }
        const asStr = String(raw).trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
        const ms = Number(raw);
        if (!Number.isFinite(ms) || ms <= 0) return "-";
        const formatted = TimeStamptoExp.formatDateFromTimestamp(ms);
        if (
          !formatted ||
          String(formatted).toLowerCase().includes("invalid")
        ) {
          return "-";
        }
        return formatted;
      }
      case "inceptionDate":
        return data?.inceptionDate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.inceptionDate.toString()) // Check if the transactionDate is in DD/MM/YYYY format
            ? data.inceptionDate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(Number(data.inceptionDate)) // Otherwise, format it
          : "-";
      case "asOfDate":
        return data?.asOfDate
          ? /^\d{2}\/\d{2}\/\d{4}$/.test(data.asOfDate.toString()) // Check if the transactionDate is in DD/MM/YYYY format
            ? data.asOfDate // If already in correct format, display the date as is
            : TimeStamptoExp.formatDateFromTimestamp(Number(data.asOfDate)) // Otherwise, format it
          : "-";
      case "dateOfInvestment": {
        const raw = data?.dateOfInvestment;
        if (
          raw == null ||
          raw === "" ||
          raw === "-" ||
          raw === 0 ||
          raw === "0"
        ) {
          return "-";
        }
        const asStr = String(raw).trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
        const ms = Number(raw);
        if (!Number.isFinite(ms) || ms <= 0) return "-";
        const formatted = TimeStamptoExp.formatDateFromTimestamp(ms);
        if (
          !formatted ||
          String(formatted).toLowerCase().includes("invalid")
        ) {
          return "-";
        }
        return formatted;
      }
      case "investorDisplay": {
        const display =
          data?.investorDisplay != null &&
          String(data.investorDisplay).trim() !== ""
            ? String(data.investorDisplay)
            : "-";
        const email = String(data?.investorEmail ?? data?.email ?? "").trim();
        const mobile = String(data?.investorMobile ?? data?.mobile ?? "").trim();
        const hasContact = Boolean(email || mobile);
        const tooltipContent = (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25 }}>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.4 }}>
              Email: {email || "-"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.4 }}>
              Mobile: {mobile || "-"}
            </Typography>
          </Box>
        );

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              width: "100%",
            }}
          >
            <span
              className="text-gray-800 break-words"
              style={{ flex: 1, minWidth: 0 }}
            >
              {display}
            </span>
            {hasContact ? (
              <Tooltip title={tooltipContent} arrow placement="top">
                <InfoOutlined
                  sx={{
                    fontSize: 16,
                    color: REPORT_COLORS.mutedText,
                    flexShrink: 0,
                    cursor: "help",
                    "&:hover": { color: REPORT_COLORS.button },
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
        );
      }
      case "memberName":
        return (
          <span className="text-gray-800 break-words">
            {data?.memberName != null && String(data.memberName).trim() !== ""
              ? String(data.memberName)
              : "-"}
          </span>
        );
      case "schemeName":
      case "symbol":
      case "holdingName":
      case "promoter":
      case "sector": {
        const toTitleCase = (str: string) => {
          // Add space before capital letters in the middle of words
          const withSpaces = str.replace(/([a-z])([A-Z])/g, '$1 $2');
          return withSpaces
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        };
        const rawValue =
          key === "sector"
            ? data?.sector ?? data?.Sector ?? "-"
            : data?.schemeName ||
              data?.symbol ||
              data?.holdingName ||
              data?.promoter ||
              "-";
        const displayName = rawValue !== "-" ? toTitleCase(String(rawValue)) : rawValue;
        
        // For sector, return simple text without icon
        if (key === "sector") {
          return <span className="text-gray-800 break-words">{displayName}</span>;
        }
        
        // For schemeName/holdingName, return with icon
        return (
          <div
            className={`flex gap-x-2 min-w-0 ${
              isReportTable ? "items-start" : "items-center"
            }`}
          >
            {initials && (
              <span className="flex w-8 h-8 items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 rounded-full text-primary font-semibold text-xs flex-shrink-0">
                {getInitials(
                  (data?.schemeName ||
                    data?.symbol ||
                    data?.holdingName ||
                    data?.promoter ||
                    "") as string,
                )}
              </span>
            )}
            <span
              className="text-gray-800"
              style={
                isReportTable ? REPORT_SCHEME_NAME_CELL_STYLE : undefined
              }
            >
              {displayName}
            </span>
          </div>
        );
      }
      case "currentValue":
      case "currentVal":
      case "currentAmount": {
        const raw = key === "currentAmount" ? data?.currentAmount : data?.[key];
        if (raw === "-" || raw === "" || raw == null) return "-";
        if (isReportTable) return formatReportTableInrCell(raw);
        return (
          <span
            className={`font-medium whitespace-nowrap ${
              key === "currentAmount" ? "text-gray-900" : "text-gray-700"
            }`}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            ₹
            {Number(raw || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
      case "investedAmount":
        return formatReportTableInrCell(data?.investedAmount);
      case "costValue":
        return formatReportTableInrCell(data?.[key]);
      case "adjustedCostPrice":
        return formatReportTableInrCell(data?.adjustedCostPrice);
      case "unrealisedPNL":
      case "unrealisedPnL":
      case "realisedPnl":
      case "realisedPnL":
      case "realisedPNL":
      case "realizedPnl":
      case "realizedPnL":
      case "realizedPNL":
      case "absolutePL":
      case "gainLoss":
      case "cuurentPnL": {
        // Prefer the column's own field. Do not fall back to gainLoss for
        // realised/unrealised keys — sell rows may carry gainLoss: "-" / holding P&L.
        const raw =
          key === "gainLoss" || key === "cuurentPnL" || key === "absolutePL"
            ? data?.gainLoss ??
              data?.cuurentPnL ??
              data?.realisedPnl ??
              data?.realisedPnL ??
              data?.realisedPNL ??
              data?.[key]
            : data?.[key] ??
              data?.realisedPnl ??
              data?.realisedPnL ??
              data?.realisedPNL ??
              data?.unrealisedPnL ??
              data?.unrealisedPNL;
        if (raw === "-" || raw === "" || raw == null) return "-";
        const pnl = Number(raw);
        if (!Number.isFinite(pnl)) return "-";
        return renderPnlAmount(pnl);
      }

      case "nifty50GainLoss":
      case "bseSensexGainLoss": {
        const raw = resolveTxnBenchmarkGainValue(data, key);
        if (raw === "-" || raw === "" || raw == null) return "-";
        const pnl = Number(raw);
        if (!Number.isFinite(pnl)) return "-";
        return renderPnlAmount(pnl);
      }

      case "absolutePnL": {
        const absoluteValue = data?.[key];
        if (typeof absoluteValue === "string" && absoluteValue.includes("%")) {
          const numValue = parseFloat(absoluteValue.replace("%", ""));
          if (isNaN(numValue)) return absoluteValue;
          return renderPnlPercent(numValue);
        }
        const numValue = Number(absoluteValue || 0);
        return renderPnlAmount(numValue);
      }
      case "unrealisedPNLPercentage":
      case "unrealisedPnLPercentage":
      case "realisedPNLPercentage":
      case "realisedPnLPercentage":
      case "realizedPNLPercentage":
      case "realizedPnLPercentage":
      case "currentPnLinPercentage": {
        const percentValue = data?.[key];
        if (percentValue === "-" || percentValue === "" || percentValue == null) {
          return "-";
        }
        let numPercent: number;
        if (typeof percentValue === "string" && percentValue.includes("%")) {
          numPercent = parseFloat(percentValue.replace("%", ""));
        } else {
          numPercent = Number(percentValue);
        }
        if (!Number.isFinite(numPercent)) return "-";
        return renderPnlPercent(numPercent);
      }
      case "nifty50GainLossPercentage":
      case "bseSensexGainLossPercentage":
      case "niftyMidCap100GainLossPercentage":
      case "nifty500GainLossPercentage":
      case "niftyBankGainLossPercentage":
      case "niftyNext50GainLossPercentage": {
        const percentValue = resolveTxnBenchmarkPercentValue(data, key);
        if (percentValue === "-" || percentValue === "" || percentValue == null) {
          return "-";
        }
        let numPercent: number;
        if (typeof percentValue === "string" && percentValue.includes("%")) {
          numPercent = parseFloat(percentValue.replace("%", ""));
        } else {
          numPercent = Number(percentValue);
        }
        if (!Number.isFinite(numPercent)) return "-";
        return renderPnlPercent(numPercent);
      }
      case "percentageShare":
      case "exposurePct": {
        const raw =
          key === "exposurePct"
            ? data?.exposurePct ?? data?.exposure ?? data?.percentageShare
            : data?.percentageShare ?? data?.exposure ?? data?.exposurePct;
        if (raw == null || raw === "" || raw === "-") return "-";
        const num = Number(String(raw).replace(/%/g, "").trim());
        if (!Number.isFinite(num)) return String(raw);
        const formatted = num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        if (isPmsBreakdown) {
          const scale = maxPmsExposurePct > 0 ? maxPmsExposurePct : 100;
          const barPct = Math.min(Math.max((num / scale) * 100, 0), 100);
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                  minWidth: 52,
                  color: REPORT_COLORS.mutedText,
                }}
              >
                {formatted}%
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  height: 8,
                }}
              >
                <span
                  style={{
                    display: "block",
                    width: `${barPct}%`,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: REPORT_COLORS.button,
                    minWidth: num > 0 ? 4 : 0,
                  }}
                />
              </span>
            </span>
          );
        }
        return (
          <span
            style={{
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
              display: "block",
              textAlign: "right",
            }}
          >
            {formatted}%
          </span>
        );
      }
      case "units":
      case "purchaseQty":
        return (
          <span style={unitsNavStyle}>
            {Number(data?.[key] || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        );
      case "closingBalance":
        return (
          <span style={unitsNavStyle}>
            {Number(data?.[key] || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        );
      case "averagePrice":
      case "averageNav":
      case "avgPrice": {
        const priceNum = parseReportTableInrAmount(data?.[key]);
        if (priceNum === null) return "-";
        return (
          <span style={{ ...unitsNavStyle, whiteSpace: "nowrap" }}>
            ₹
            {priceNum.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
      case "currentNAV":
      case "purchaseNAV":
      case "purchaseRateNav":
      case "currentRateNav":
      case "currentPrice":
      case "purchasePrice":
      case "avgBuyPrice": {
        const raw =
          key === "avgBuyPrice"
            ? data?.avgBuyPrice ??
              data?.averageBuyPrice ??
              data?.avgPurchasePrice
            : data?.[key];
        if (raw === "-" || raw === "" || raw == null) return "-";
        const num = Number(raw);
        if (!Number.isFinite(num)) return "-";
        return (
          <span style={{ ...unitsNavStyle, whiteSpace: "nowrap" }}>
            ₹
            {num.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
      case "unrealisedPNLCombined":
      case "realisedPNLCombined": {
        if (isReportTable) {
          return renderCombinedPnlCell(
            data as Record<string, unknown>,
            key as "unrealisedPNLCombined" | "realisedPNLCombined",
          );
        }
        const raw = data?.[key];
        if (raw == null || raw === "" || raw === "-") return "-";
        if (typeof raw === "string" && /NaN/i.test(raw)) return "-";
        return raw;
      }
      case "folioNo": {
        const folio =
          data?.folioNo ?? data?.folioNumber ?? data?.folio ?? "-";
        if (folio === "-" || folio == null) return "-";
        if (isMfP2pTable) {
          return (
            <span style={{ whiteSpace: "nowrap", display: "inline-block" }}>
              {String(folio)}
            </span>
          );
        }
        return String(folio);
      }
      case "Gain/Loss": {
        const raw = data?.[key];
        if (raw === "-" || raw === "" || raw == null) return "-";
        const pnl = Number(raw);
        if (!Number.isFinite(pnl)) return "-";
        return renderPnlAmount(pnl);
      }
      case "Qty Sold":
        return (
          <span style={unitsNavStyle}>
            {Number(data?.[key] || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            })}
          </span>
        );
      case "Sale Price":
      case "Purchase Price":
        return (
          <span style={{ ...unitsNavStyle, whiteSpace: "nowrap" }}>
            {Number(data?.[key] || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      case "Holding Period (Days)":
        return (
          <span style={{ ...unitsNavStyle, whiteSpace: "nowrap" }}>
            {Number(data?.[key] || 0).toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </span>
        );
      case "days": {
        const raw = data?.days;
        if (
          raw == null ||
          raw === "" ||
          raw === "-" ||
          (typeof raw === "number" && !Number.isFinite(raw))
        ) {
          return "-";
        }
        const num = parseReportTableInrAmount(raw);
        if (num === null || !Number.isFinite(num)) return "-";
        return (
          <span
            className="font-medium text-gray-900 whitespace-nowrap"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {Math.round(num).toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </span>
        );
      }
      case "Asset Name":
        return (
          <span className="text-gray-800" style={{ wordBreak: "break-word" }}>
            {data?.[key] != null && String(data[key]).trim() !== ""
              ? String(data[key])
              : "-"}
          </span>
        );
      case "name": {
        const label =
          data?.name != null && String(data.name).trim() !== ""
            ? String(data.name)
            : "-";
        const isScheme = data?._rowKind === "scheme";
        const isStock = data?._rowKind === "stock";
        return (
          <span
            style={{
              wordBreak: "break-word",
              display: "block",
              fontWeight: isScheme ? 700 : 400,
              paddingLeft: isStock ? 20 : 0,
              color: isScheme
                ? REPORT_COLORS.primaryText
                : REPORT_COLORS.primaryText,
            }}
          >
            {label}
          </span>
        );
      }
      default:
        return data && data[key] !== undefined && data[key] !== null
          ? data[key]
          : "-";
    }
  };

  if (isReportTable && isMobileCardView) {
    return (
      <MfReportCardList
        bodyData={bodyData}
        columns={headData}
        renderCell={(key: string, row: Record<string, unknown>) => formatCellData(key, row)}
      />
    );
  }

  return (
    <TableContainer
      component={isReportTable ? "div" : Paper}
      elevation={0}
      sx={
        isReportTable
          ? {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
              minHeight: useReportFreezeHeader ? 0 : undefined,
              boxShadow: "none",
              border: "none",
              borderRadius: 0,
            }
          : {
              overflowX: "auto",
              boxShadow: "none",
              border: "none",
              borderRadius: 0,
              width: "100%",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { height: "8px" },
              "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#cbd5e1",
                borderRadius: "4px",
              },
            }
      }
    >
      <Table
        aria-label="collapsible table"
        stickyHeader={useReportFreezeHeader}
        sx={{
          ...(isReportTable
            ? {
                ...REPORT_TABLE_ELEMENT_SX,
                tableLayout: useBreakdownTableLayout ? "fixed" : "auto",
                borderCollapse: "separate",
                borderSpacing: 0,
              }
            : {
                tableLayout: useBreakdownTableLayout ? "fixed" : "auto",
                width: "100%",
              }),
        }}
      >
        <TableHead
          sx={
            isReportTable
              ? {
                  ...REPORT_TABLE_HEAD_SX,
                  ...(useReportFreezeHeader && {
                    "& .MuiTableCell-stickyHeader": {
                      backgroundColor: `${REPORT_COLORS.tableHeader} !important`,
                      backgroundImage: "none",
                    },
                  }),
                  "& th": {
                    ...REPORT_TABLE_HEAD_SX["& th"],
                    ...(useReportFreezeHeader
                      ? {
                          borderBottom: `2px solid ${REPORT_COLORS.tableHeader}`,
                        }
                      : {}),
                    textTransform: "none",
                    padding: useBreakdownTableLayout ? "14px 12px" : "14px 20px",
                  },
                }
              : {
                  backgroundColor: tableHeaderBg,
                  borderBottom: "1px solid #E5E7EB",
                  "& th": {
                    backgroundColor: tableHeaderBg,
                    fontWeight: 600,
                    color: tableHeaderText,
                    fontSize: "13px",
                    letterSpacing: "0.02em",
                    borderBottom: "1px solid #E5E7EB",
                    padding: useBreakdownTableLayout ? "14px 12px" : "14px 20px",
                  },
                }
          }
        >
          <TableRow
            sx={
              isReportTable
                ? REPORT_TABLE_HEAD_ROW_SX
                : {
                    borderBottom: "none",
                    backgroundColor: tableHeaderBg,
                    "& th": {
                      backgroundColor: tableHeaderBg,
                      border: "none",
                      color: tableHeaderText,
                    },
                  }
            }
          >
            {headData.map((col, idx) => {
              const { key, value } = col;
              const isNumericColumn = [
                "amount", "value", "investedAmount", "currentAmount", "currentValue",
                "adjustedCostPrice", "costValue", "units", "purchaseQty", "closingBalance", "currentNAV", "purchaseNAV",
                "purchaseRateNav", "currentRateNav", "absolutePL", "investedAmount",
                "gainLoss", "gainLossPercentage", "unrealisedPNL", "unrealisedPNLPercentage",
                "nifty50GainLoss", "nifty50GainLossPercentage", "bseSensexGainLoss", "bseSensexGainLossPercentage",
                "niftyMidCap100GainLossPercentage", "nifty500GainLossPercentage",
                "niftyBankGainLossPercentage", "niftyNext50GainLossPercentage",
                "cuurentPnL", "currentPnLinPercentage", "gainLossPerc", "absolutePnL",
                "absoluteReturn", "absoluteReturnPerc", "monthlyReturn", "monthlyReturnPercentage",
                "weekelyReturn", "weekelyReturnPercentage", "weeklyReturn", "weeklyReturnPercentage",
                "cagr", "marketValue",
                "benchmarkAR", "returnAmount", "netPurchase", "grossPurchase",
                "currentVal", "roi", "interesetRate", "interestRate", "grossInterest",
                "percentageShare", "exposurePct",
              ].includes(key);
              
              // Add border to separate column groups
              const needsLeftBorder = !useBreakdownTableLayout && ["investedAmount", "gainLoss"].includes(key);
              
              const getTextAlign = () => {
                if ((headData[idx] as any)?.align) return (headData[idx] as any).align;
                return isNumericColumn ? "right" : "left";
              };
              
              return (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: tableHeaderText,
                    fontFamily: PROFILE_FONT,
                    fontSize: "13px",
                    whiteSpace: TXN_COST_COLUMN_KEY_SET.has(key)
                      ? "nowrap"
                      : "normal",
                    wordWrap: TXN_COST_COLUMN_KEY_SET.has(key)
                      ? "normal"
                      : "break-word",
                    textAlign: getTextAlign(),
                    width: getSectorReportColumnWidth(
                      isSectorReport,
                      headData,
                      key,
                      segment,
                      reportType,
                    ),
                    minWidth: isMfTxnReport || isStockP2pTable
                      ? getMfTxnReportColumnMinWidth(key, col, reportType)
                      : isPmsBreakdown
                        ? col?.minWidth
                          ? `${col.minWidth}px`
                          : undefined
                      : !isSectorReport || hasTxnCostColumns
                        ? key === "investmentFrequency" || key === "amcCode"
                          ? "150px"
                          : key === "schemeName" ||
                              key === "holdingName" ||
                              key === "insurerName" ||
                              key === "primaryCategory" ||
                              key === "promoter" ||
                              key === "transactiondate"
                            ? "200px"
                            : key === "type"
                              ? "140px"
                              : col?.minWidth
                                ? `${col.minWidth}px`
                                : "120px"
                        : undefined,
                    maxWidth:
                      isMfTxnReport &&
                      !isAnyP2pTable &&
                      !isTxnBenchmarkColumn(key)
                        ? getMfTxnReportColumnMinWidth(key, col, reportType)
                        : undefined,
                    ...(isTxnBenchmarkColumn(key)
                      ? {
                          ...TXN_BENCHMARK_CELL_SX,
                          whiteSpace: "pre-line",
                          textAlign: "right",
                          lineHeight: 1.25,
                        }
                      : {}),
                    ...(isReportTable &&
                    (key === "schemeName" ||
                      key === "holdingName" ||
                      key === "promoter")
                      ? {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: 1.35,
                        }
                      : {}),
                    paddingTop: useBreakdownTableLayout ? "14px" : "12px",
                    paddingBottom: useBreakdownTableLayout ? "14px" : "12px",
                    paddingLeft: useBreakdownTableLayout
                      ? "12px"
                      : isAnyP2pTable || isMfTxnReport
                        ? "12px"
                        : "20px",
                    paddingRight: useBreakdownTableLayout
                      ? "12px"
                      : isAnyP2pTable || isMfTxnReport
                        ? "12px"
                        : "20px",
                    borderLeft: needsLeftBorder ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
                    ...getSectorReportCellSx(isSectorReport, key, isPmsBreakdown),
                    ...getMfP2pColumnSpacingSx(isMfP2pTable, key),
                    ...(useReportStickyColumns
                      ? getReportStickyColumnSx(
                          headData,
                          idx,
                          tableHeaderBg,
                          Z_TABLE_STICKY_COL_HEAD,
                          {
                            freezeHeaderRow: useReportFreezeHeader,
                            isHeaderCell: true,
                          },
                        )
                      : useReportFreezeHeader
                        ? getReportStickyColumnSx(
                            headData,
                            idx,
                            tableHeaderBg,
                            Z_TABLE_STICKY_HEAD_ROW,
                            {
                              freezeHeaderRow: true,
                              isHeaderCell: true,
                            },
                          )
                        : {}),
                  }}
                  key={key}
                >
                  <SortingComponent
                    title={value}
                    data={bodyData}
                    keyProp={key}
                    onSort={unSortdata}
                    align={getTextAlign()}
                    headerTone={tableHeaderTone}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ border: "none" }} className="">
          {bodyData &&
            bodyData
              .filter((row) => row !== null && row !== undefined)
              .map((data, index) => {
                const consolidatedRowKind =
                  isConsolidated && data?._rowKind === "scheme"
                    ? "scheme"
                    : isConsolidated && data?._rowKind === "stock"
                      ? "stock"
                      : null;
                const consolidatedRowBg = REPORT_COLORS.white;
                return (
                <React.Fragment key={index}>
                  <TableRow
                    sx={
                      isReportTable
                        ? {
                            ...REPORT_TABLE_ROW_SX,
                            ...(consolidatedRowKind
                              ? {
                                  backgroundColor: consolidatedRowBg,
                                  "& td": {
                                    ...((REPORT_TABLE_ROW_SX as any)["& td"] ??
                                      {}),
                                    fontWeight:
                                      consolidatedRowKind === "scheme"
                                        ? 700
                                        : 400,
                                    backgroundColor: consolidatedRowBg,
                                  },
                                }
                              : null),
                          }
                        : {
                      backgroundColor: "#ffffff",
                      transition: "all 0.15s ease",
                      borderBottom: "1px solid #f0f0f0",
                      "&:hover": {
                        backgroundColor: "rgba(30, 58, 95, 0.04)",
                        boxShadow: "inset 3px 0 0 #1E3A5F",
                        "& td": {
                          backgroundColor: "rgba(30, 58, 95, 0.04)",
                        },
                      },
                    }}
                  >
                    {headData.map((col, idx) => {
                      const { key } = col;
                      return key === "action" ? (
                        <TableCell sx={{ border: "none" }}>
                          <IconButton
                            onClick={(e) => {
                              if (data?.id !== undefined && data?.id !== null) {
                                modalMenuClick(e, data.id);
                              }
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              data?.id !== undefined && handleToggle(data.id)
                            }
                          >
                            {data?.id !== undefined && open[data.id] ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </IconButton>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={key}
                          sx={{
                            border: "none!important",
                            padding: useBreakdownTableLayout
                              ? "15px 12px"
                              : isAnyP2pTable
                                ? "14px 12px"
                                : isMfTxnReport
                                  ? "14px 12px"
                                  : "18px 20px",
                            fontFamily: PROFILE_FONT,
                            fontSize: "14px",
                            color: isReportTable
                              ? REPORT_COLORS.primaryText
                              : "#111827",
                            fontWeight: 400,
                            verticalAlign:
                              (isMfTxnReport && key === "schemeName") ||
                              (isReportTable &&
                                (key === "schemeName" ||
                                  key === "holdingName" ||
                                  key === "promoter"))
                                ? "top"
                                : "middle",
                            ...((isMfTxnReport || isStockP2pTable) &&
                              getMfTxnReportCellWidthSx(
                                key,
                                col,
                                reportType,
                                segment,
                              )),
                            ...(isTxnBenchmarkColumn(key)
                              ? { ...TXN_BENCHMARK_CELL_SX, textAlign: "right" }
                              : {}),
                            ...(isReportTable &&
                            (key === "schemeName" ||
                              key === "holdingName" ||
                              key === "promoter")
                              ? {
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  lineHeight: 1.35,
                                }
                              : {}),
                            ...(isMfTxnReport &&
                              MF_TXN_REPORT_WRAP_KEYS.has(key) &&
                              key !== "schemeName" &&
                              !(isMfP2pTable && key === "folioNo") && {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: 1.4,
                              }),
                            ...(isMfTxnReport &&
                              MF_TXN_TYPE_COLUMN_KEYS.has(key) && {
                                overflow: "visible",
                                textOverflow: "unset",
                              }),
                            textAlign: (headData[idx] as any)?.align || ([
                              "amount", "value", "investedAmount", "currentAmount", "currentValue",
                              "costValue", "units", "purchaseQty", "closingBalance", "currentNAV", "purchaseNAV",
                              "purchaseRateNav", "currentRateNav", "absolutePL",
                              "gainLoss", "gainLossPercentage", "unrealisedPNL", "unrealisedPNLPercentage",
                              "cuurentPnL", "currentPnLinPercentage", "gainLossPerc", "absolutePnL",
                              "absoluteReturn", "absoluteReturnPerc", "monthlyReturn", "monthlyReturnPercentage",
                              "weekelyReturn", "weekelyReturnPercentage", "weeklyReturn", "weeklyReturnPercentage",
                "cagr", "marketValue",
                              "benchmarkAR", "returnAmount", "netPurchase", "grossPurchase",
                              "currentVal", "roi", "interesetRate", "interestRate", "grossInterest",
                              "percentageShare", "exposurePct",
                            ].includes(key) ? "right" : "left"),
                            borderLeft: !useBreakdownTableLayout && ["investedAmount", "gainLoss"].includes(key) 
                              ? "1px solid #f0f0f0" 
                              : "none",
                            ...getSectorReportCellSx(isSectorReport, key, isPmsBreakdown),
                            ...getMfP2pColumnSpacingSx(isMfP2pTable, key),
                            ...(useReportStickyColumns
                              ? getReportStickyColumnSx(
                                  headData,
                                  idx,
                                  REPORT_COLORS.white,
                                  Z_TABLE_STICKY_COL_BODY,
                                )
                              : {}),
                          }}
                          className={`text-wrap ${
                            key === "providerName" ||
                            key === "amcCode" ||
                            key === "investmentFrequency"
                              ? "max-w-2 overflow-hidden text-wrap "
                              : "w-auto"
                          }`}
                        >
                          {formatCellData(key, data)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {data?.schemes && (
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={headData.length + 1}
                      >
                        <Collapse
                          in={data?.id !== undefined && open[data.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Table>
                            <TableBody>
                              {data.schemes
                                .filter(
                                  (s: any) => s !== null && s !== undefined,
                                )
                                .map((scheme: any, i: number) => (
                                  <TableRow key={scheme?.id ?? i}>
                                    {headData.map(({ key }) => (
                                      <TableCell key={key}>
                                        {scheme &&
                                        scheme[key] !== undefined &&
                                        scheme[key] !== null
                                          ? scheme[key]
                                          : "-"}
                                      </TableCell>
                                    ))}
                                    <TableCell
                                      onClick={() =>
                                        scheme?.id !== undefined &&
                                        navigateDetails(scheme.id)
                                      }
                                    >
                                      {scheme?.schemeName || "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MainTableLayout;
